import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { BugReport, CheckCircle, Delete, DeleteSweep, Refresh, ReportProblem } from '@mui/icons-material'
import { errorLogService } from '../services/api'
import { ErrorLogRecord } from '../types'
import { useStore } from '../store/authStore'

const unwrapData = <T,>(response: any): T => (response?.data ?? response) as T

const formatDateTime = (value?: string | null) => {
  if (!value) return 'غير متوفر'
  return new Date(value).toLocaleString('ar-IQ', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const getSourceColor = (source: string) => {
  switch (source.toLowerCase()) {
    case 'api':
      return 'error'
    case 'web':
      return 'info'
    case 'mobile':
      return 'secondary'
    default:
      return 'default'
  }
}

export default function ErrorsPage() {
  const { user } = useStore()
  const isOwner = user?.role === 'Owner'
  const [rows, setRows] = useState<ErrorLogRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [actionId, setActionId] = useState<number | null>(null)
  const [bulkBusy, setBulkBusy] = useState(false)
  const [feedback, setFeedback] = useState<{ severity: 'success' | 'error'; message: string } | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'fixed'>('all')
  const [sourceFilter, setSourceFilter] = useState<'all' | string>('all')
  const [selectedIds, setSelectedIds] = useState<number[]>([])

  const fetchErrors = async () => {
    try {
      setLoading(true)
      const response = await errorLogService.getRecent(200)
      setRows(unwrapData<ErrorLogRecord[]>(response) ?? [])
      setSelectedIds([])
    } catch (error: any) {
      setFeedback({ severity: 'error', message: error?.response?.data?.message || 'فشل في تحميل سجل الأخطاء' })
      setRows([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchErrors()
  }, [])

  const filteredRows = useMemo(() => {
    const query = searchTerm.trim().toLowerCase()

    return rows.filter((row) => {
      const matchesSearch = !query || [
        row.customerName,
        row.deviceCode,
        row.mainDeviceCode,
        row.errorMessage,
        row.requestPath,
        row.formOrEndpoint,
        row.userName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query)

      const matchesStatus = statusFilter === 'all'
        || (statusFilter === 'open' && !row.isFixed)
        || (statusFilter === 'fixed' && row.isFixed)

      const matchesSource = sourceFilter === 'all' || row.source === sourceFilter
      return matchesSearch && matchesStatus && matchesSource
    })
  }, [rows, searchTerm, statusFilter, sourceFilter])

  const sourceOptions = useMemo(() => {
    return Array.from(new Set(rows.map((row) => row.source).filter(Boolean)))
  }, [rows])

  const handleFix = async (errorId: number) => {
    try {
      setActionId(errorId)
      await errorLogService.markFixed(errorId, {})
      setFeedback({ severity: 'success', message: 'تم تعليم الخطأ كمحلول' })
      await fetchErrors()
    } catch (error: any) {
      setFeedback({ severity: 'error', message: error?.response?.data?.message || 'تعذر تحديث حالة الخطأ' })
    } finally {
      setActionId(null)
    }
  }

  const handleReport = async (errorId: number) => {
    try {
      setActionId(errorId)
      await errorLogService.markReported(errorId, {})
      setFeedback({ severity: 'success', message: 'تم تبليغ الخطأ بنجاح' })
      await fetchErrors()
    } catch (error: any) {
      setFeedback({ severity: 'error', message: error?.response?.data?.message || 'تعذر تبليغ الخطأ' })
    } finally {
      setActionId(null)
    }
  }

  const handleDelete = async (errorId: number) => {
    if (!window.confirm('هل تريد حذف هذا الخطأ نهائياً؟')) return

    try {
      setActionId(errorId)
      await errorLogService.delete(errorId)
      setFeedback({ severity: 'success', message: 'تم حذف الخطأ' })
      await fetchErrors()
    } catch (error: any) {
      setFeedback({ severity: 'error', message: error?.response?.data?.message || 'تعذر حذف الخطأ' })
    } finally {
      setActionId(null)
    }
  }

  const visibleIds = useMemo(() => filteredRows.map((row) => row.errorId), [filteredRows])
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id))
  const someVisibleSelected = visibleIds.some((id) => selectedIds.includes(id))

  const toggleOne = (errorId: number) => {
    setSelectedIds((prev) => (prev.includes(errorId) ? prev.filter((id) => id !== errorId) : [...prev, errorId]))
  }

  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      if (allVisibleSelected) return prev.filter((id) => !visibleIds.includes(id))
      return Array.from(new Set([...prev, ...visibleIds]))
    })
  }

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) {
      setFeedback({ severity: 'error', message: 'حدّد خطأ واحداً على الأقل' })
      return
    }
    if (!window.confirm(`حذف ${selectedIds.length} خطأ نهائياً؟ (حتى غير المبلّغ عنها)`)) return
    try {
      setBulkBusy(true)
      const response = await errorLogService.deleteMany(selectedIds)
      const deleted = typeof response?.data === 'number' ? response.data : selectedIds.length
      setFeedback({ severity: 'success', message: response?.message || `تم حذف ${deleted} خطأ` })
      setSelectedIds([])
      await fetchErrors()
    } catch (error: any) {
      setFeedback({ severity: 'error', message: error?.response?.data?.message || 'تعذر حذف الأخطاء المحددة' })
    } finally {
      setBulkBusy(false)
    }
  }

  const handleClearFixed = async () => {
    const fixedCount = rows.filter((row) => row.isFixed).length
    if (fixedCount === 0) {
      setFeedback({ severity: 'error', message: 'لا توجد أخطاء معالجة لحذفها' })
      return
    }
    if (!window.confirm(`هل تريد حذف كل الأخطاء المعالجة (${fixedCount})؟`)) return

    try {
      setBulkBusy(true)
      const response = await errorLogService.clearFixed()
      const deleted = typeof response?.data === 'number' ? response.data : (response?.deleted ?? fixedCount)
      setFeedback({ severity: 'success', message: response?.message || `تم حذف ${deleted} خطأ معالج` })
      await fetchErrors()
    } catch (error: any) {
      setFeedback({ severity: 'error', message: error?.response?.data?.message || 'تعذر حذف الأخطاء المعالجة' })
    } finally {
      setBulkBusy(false)
    }
  }

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>
            إدارة سجل الأخطاء
          </Typography>
          <Typography color="text.secondary">
            شاشة مستقلة لمراجعة أخطاء العملاء والأجهزة الرئيسية والفرعية ومعالجة كل خطأ بشكل احترافي.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
          {isOwner && (
            <>
              <Button
                variant="contained"
                color="error"
                startIcon={<Delete />}
                onClick={handleDeleteSelected}
                disabled={loading || bulkBusy || selectedIds.length === 0}
              >
                حذف المحدد ({selectedIds.length})
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteSweep />}
                onClick={handleClearFixed}
                disabled={loading || bulkBusy}
              >
                حذف المعالجة
              </Button>
            </>
          )}
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchErrors} disabled={loading}>
            تحديث
          </Button>
        </Stack>
      </Stack>

      {feedback && (
        <Alert severity={feedback.severity} onClose={() => setFeedback(null)} sx={{ mb: 3, borderRadius: 3 }}>
          {feedback.message}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6} lg={7}>
          <TextField
            fullWidth
            label="بحث بالعميل أو الجهاز أو الرسالة أو المسار"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={3} lg={2.5}>
          <TextField
            select
            fullWidth
            label="الحالة"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as 'all' | 'open' | 'fixed')}
          >
            <MenuItem value="all">كل الحالات</MenuItem>
            <MenuItem value="open">مفتوحة</MenuItem>
            <MenuItem value="fixed">معالجة</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} md={3} lg={2.5}>
          <TextField
            select
            fullWidth
            label="المصدر"
            value={sourceFilter}
            onChange={(event) => setSourceFilter(event.target.value)}
          >
            <MenuItem value="all">كل المصادر</MenuItem>
            {sourceOptions.map((source) => (
              <MenuItem key={source} value={source}>
                {source}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mb: 3 }}>
        <Chip label={`الإجمالي: ${rows.length}`} variant="outlined" />
        <Chip label={`المفتوحة: ${rows.filter((row) => !row.isFixed).length}`} color="error" />
        <Chip label={`المعالجة: ${rows.filter((row) => row.isFixed).length}`} color="success" />
        <Chip label={`المبلغ عنها: ${rows.filter((row) => row.isReportedToHaizoom).length}`} color="info" />
      </Stack>

      <Paper sx={{ borderRadius: 4, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : filteredRows.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <BugReport sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.4, mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
              لا توجد أخطاء تطابق الفلاتر الحالية
            </Typography>
            <Typography color="text.secondary">
              جرّب تغيير البحث أو الحالة أو المصدر.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table sx={{ minWidth: 1250 }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allVisibleSelected}
                      indeterminate={!allVisibleSelected && someVisibleSelected}
                      onChange={toggleAllVisible}
                      disabled={!isOwner || visibleIds.length === 0}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>الوقت</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>العميل</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>الجهاز</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>المصدر</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>الرسالة</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>المسار</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>الحالة</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>التبليغ</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>الإجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((row) => (
                  <TableRow key={row.errorId} hover selected={selectedIds.includes(row.errorId)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.includes(row.errorId)}
                        onChange={() => toggleOne(row.errorId)}
                        disabled={!isOwner}
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(row.errorTime)}</TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.customerName || 'غير مربوط'}</Typography>
                        <Typography variant="caption" color="text.secondary">{row.userName || 'بدون مستخدم'}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.deviceCode || row.computerNo || '-'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.deviceType === 'Main'
                            ? 'جهاز رئيسي'
                            : row.deviceType === 'Sub'
                              ? `جهاز فرعي${row.mainDeviceCode ? ` / الرئيسي: ${row.mainDeviceCode}` : ''}`
                              : 'غير مربوط'}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip label={row.source} size="small" color={getSourceColor(row.source) as any} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                      >
                        {row.errorMessage}
                      </Typography>
                      {row.errorType && (
                        <Typography variant="caption" color="text.secondary">
                          النوع: {row.errorType}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography variant="body2">{row.formOrEndpoint || row.requestPath || '-'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.httpMethod || ''} {row.methodName || ''}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {row.isFixed ? (
                        <Chip label={row.fixedBy ? `تم الإصلاح بواسطة ${row.fixedBy}` : 'تم الإصلاح'} color="success" size="small" />
                      ) : (
                        <Chip label="مفتوح" color="error" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      {row.isReportedToHaizoom ? (
                        <Chip label={row.reportedBy ? `أبلغ: ${row.reportedBy}` : 'تم التبليغ'} color="info" size="small" />
                      ) : (
                        <Chip label="غير مبلغ" variant="outlined" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          disabled={row.isFixed || actionId === row.errorId}
                          startIcon={<CheckCircle />}
                          onClick={() => handleFix(row.errorId)}
                        >
                          {actionId === row.errorId ? '...' : 'تم الإصلاح'}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="info"
                          disabled={row.isReportedToHaizoom || actionId === row.errorId}
                          startIcon={<ReportProblem />}
                          onClick={() => handleReport(row.errorId)}
                        >
                          تبليغ
                        </Button>
                        {isOwner && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={actionId === row.errorId || bulkBusy}
                            startIcon={<Delete />}
                            onClick={() => handleDelete(row.errorId)}
                          >
                            حذف
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  )
}
